import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTrack } from "../../hooks/useTrack";
import { supabase } from "../../lib/supabase";
import { Sparkline } from "../../components/Sparkline";
import { AreaChart, IMPR_COLOR, CONV_COLOR } from "../../components/AreaChart";
import { DateRangePicker } from "../../components/DateRangePicker";
import { formatImpressions, formatCurrency, formatPercent } from "../../utils/formatters";
import { getDateRangeBounds, toUTCStartEnd } from "../../utils/dateUtils";
import { getCurrentMonthFirstDay } from "../../utils/calendarHelpers";
import { PRICING_MODES } from "../../constants/ui";

const CHART_LEGEND = [
  { key: "impressions", label: "Impressions", color: IMPR_COLOR },
  { key: "conversions", label: "Conversions", color: CONV_COLOR },
];

function formatCreditDateShort(dateString) {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

// Chip with a custom portal-rendered tooltip, opened either on hover
// (native `title=` was rejected because of the ~700ms browser delay) or on
// click (for tooltips with richer/longer content the user should be able to
// keep open, e.g. the credit tag's terms). Rendered through a portal at
// document.body so it isn't clipped by ancestor overflow:hidden (the
// dashboard cards use it for the rounded corners). The position is derived
// from the chip's bounding rect on open, then re-derived on resize/scroll
// while the tooltip is visible so it tracks the chip.
function TooltipChip({ trigger = "hover", children, tooltip, style }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const contentRef = useRef(null);
  const isClick = trigger === "click";

  const recompute = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.top, left: r.left + r.width / 2 });
  }, []);

  useEffect(() => {
    if (!open) return;
    recompute();
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    if (!isClick) {
      return () => {
        window.removeEventListener("scroll", recompute, true);
        window.removeEventListener("resize", recompute);
      };
    }
    const handleOutsideClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) &&
          contentRef.current && !contentRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [open, recompute, isClick]);

  const triggerProps = isClick
    ? {
        role: "button",
        "aria-expanded": open,
        onClick: () => setOpen((v) => !v),
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        },
      }
    : {
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
        onFocus: () => setOpen(true),
        onBlur: () => setOpen(false),
      };

  return (
    <>
      <span
        ref={ref}
        tabIndex={0}
        {...triggerProps}
        style={{ ...style, cursor: isClick ? "pointer" : "help", outline:"none" }}
      >
        {children}
      </span>
      {open && pos && createPortal(
        <span
          ref={contentRef}
          role="tooltip"
          style={{
            position:"fixed",
            top: pos.top - 8,
            left: pos.left,
            transform:"translate(-50%, -100%)",
            background:"#151515",
            color:"#FFFFFF",
            fontSize:12,
            fontWeight:400,
            lineHeight:1.4,
            padding:"6px 10px",
            borderRadius:6,
            whiteSpace:"normal",
            width:"max-content",
            maxWidth:240,
            textAlign:"center",
            boxShadow:"0 3px 15px rgba(0,0,0,0.18)",
            pointerEvents: isClick ? "auto" : "none",
            zIndex:10000,
          }}
        >
          {tooltip}
        </span>,
        document.body
      )}
    </>
  );
}

function PendingChip() {
  return (
    <TooltipChip
      tooltip="Changes may take up to 48 hours to be processed"
      style={{ fontSize:11, fontWeight:600, background:"#FEF3C7", color:"#92400E", padding:"2px 8px", borderRadius:20, whiteSpace:"nowrap", flexShrink:0, display:"inline-block" }}
    >
      Pending
    </TooltipChip>
  );
}

export function DashboardScreen() {
  const { openBudget = () => {}, campaignSettingsRefresh = 0 } = useOutletContext() ?? {};
  const { effectiveAdvertiserId: advertiserId, user, userFirstName } = useAuth();
  const track = useTrack();
  const [wide, setWide] = React.useState(window.innerWidth >= 768);
  React.useEffect(() => {
    const handler = () => setWide(window.innerWidth >= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const [dateRange, setDateRange]             = useState("MTD");
  const [visibleSeries, setVisibleSeries] = useState({ impressions: true, conversions: true });
  const [appliedCustomStart, setAppliedCustomStart] = useState(null);
  const [appliedCustomEnd, setAppliedCustomEnd]     = useState(null);
  const [campaignSettings, setCampaignSettings] = useState(null);
  const [campaignSettingsLoading, setCampaignSettingsLoading] = useState(true);
  const [impressionsData, setImpressionsData] = useState({
    total: 0,
    delta: "",
    spark: [],
    loading: true,
    error: null,
  });
  const [conversionsData, setConversionsData] = useState({
    count: 0,
    revenueSum: 0,
    spendSum: 0,
    delta: "",
    loading: true,
    error: null,
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [, setTick] = useState(0); // forces re-render for relative time
  const [chartData, setChartData] = useState({
    impressionsByDay: [],
    conversionsByDay: [],
    dateLabels: [],
    loading: true,
    error: null,
  });
  // Pacing bar: MTD spend split by cap vs. credit, for the two-color segments.
  // capSpend + creditSpend together are also the pacing bar's total MTD spend
  // — there is deliberately no separate "total spend" fetch/RPC here, since
  // that would be a second, independent query for the same MTD window that
  // could race or disagree with this one.
  const [mtdCreditSplit, setMtdCreditSplit] = useState({
    capSpend: 0,
    creditSpend: 0,
    loading: true,
    error: null,
  });
  // Pacing bar: the advertiser's currently-active credit terms (if any), for the credit tag
  const [activeCredit, setActiveCredit] = useState(null);

  useEffect(() => {
    if (advertiserId == null && user?.email) {
      setImpressionsData((p) => ({ ...p, loading: false, error: "No advertiser linked" }));
    }
  }, [advertiserId, user?.email]);

  const monthYear = getCurrentMonthFirstDay();
  useEffect(() => {
    if (!supabase || advertiserId == null) {
      setCampaignSettingsLoading(false);
      return;
    }
    let cancelled = false;
    setCampaignSettingsLoading(true);
    (async () => {
      // Skip rejected rows: the client should never see a snapshot the admin
      // sent back. If the latest submission is rejected we fall through to
      // the previous approved/pending row for the same month (or nothing if
      // none exists, which surfaces the empty state). The Support Console
      // still sees rejected rows via its own queries.
      const { data: row } = await supabase
        .from("campaign_settings")
        .select("spend_cap, cpa_types, fixed_cpa, pricing_mode, status, review_notes")
        .eq("advertiser_id", advertiserId)
        .eq("month_year", monthYear)
        .in("status", ["approved", "pending"])
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      setCampaignSettings(row ?? null);
      setCampaignSettingsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [advertiserId, monthYear, campaignSettingsRefresh]);

  useEffect(() => {
    if (!supabase || advertiserId == null) {
      if (advertiserId == null && user?.email) {
        setImpressionsData((p) => ({ ...p, loading: true, error: null }));
      } else if (!supabase) {
        setImpressionsData((p) => ({ ...p, loading: false, error: "Supabase not configured" }));
      }
      return;
    }
    const { startDate, endDate } = getDateRangeBounds(dateRange, appliedCustomStart, appliedCustomEnd);
    if (startDate > endDate) {
      setImpressionsData({ total: 0, delta: "—", spark: [0], loading: false, error: null });
      return;
    }
    let cancelled = false;
    setImpressionsData((p) => ({ ...p, loading: true, error: null }));
    (async () => {
      const { data, error } = await supabase.functions.invoke("rill-impressions", {
        body: { advertiser_id: advertiserId, start_date: startDate, end_date: endDate },
      });
      if (cancelled) return;
      if (error) {
        setImpressionsData((p) => ({ ...p, loading: false, error: error.message, total: 0, delta: "", spark: [] }));
        return;
      }
      const byDate = {};
      for (const entry of data || []) {
        const d = entry.date;
        const dayTotal = (entry.campaigns || []).reduce((s, c) => s + (c.impressions ?? 0), 0);
        byDate[d] = (byDate[d] ?? 0) + dayTotal;
      }
      const sortedDates = Object.keys(byDate).sort();
      const spark = sortedDates.map((d) => byDate[d]);
      const total = spark.reduce((s, n) => s + n, 0);
      const lastDay = spark.length ? spark[spark.length - 1] : 0;
      const delta = spark.length ? `+${lastDay >= 1000 ? `${(lastDay / 1000).toFixed(1)}K` : lastDay} last day` : "—";
      setImpressionsData({
        total,
        delta,
        spark: spark.length ? spark : [0],
        loading: false,
        error: null,
      });
    })();
    return () => { cancelled = true; };
  }, [advertiserId, dateRange, appliedCustomStart, appliedCustomEnd, user?.email]);

  // Billable conversions count and revenue from assisted_conversions (is_billable = true)
  // Uses RPC so aggregation happens in DB — no row limit; count/sum are exact.
  useEffect(() => {
    if (!supabase || advertiserId == null) {
      setConversionsData((p) => ({ ...p, loading: false, error: advertiserId == null && user?.email ? "No advertiser linked" : "Supabase not configured" }));
      return;
    }
    let { startDate, endDate } = getDateRangeBounds(dateRange, appliedCustomStart, appliedCustomEnd);
    if (startDate > endDate) {
      setConversionsData({ count: 0, revenueSum: 0, spendSum: 0, delta: "—", loading: false, error: null });
      return;
    }
    const { startISO, endISO } = toUTCStartEnd(startDate, endDate);
    let cancelled = false;
    setConversionsData((p) => ({ ...p, loading: true, error: null }));
    (async () => {
      const { data, error } = await supabase.rpc("get_billable_conversions_stats_hybrid", {
        p_advertiser_id: advertiserId,
        p_start_at: startISO,
        p_end_at: endISO,
      });
      if (cancelled) return;
      if (error) {
        setConversionsData({ count: 0, revenueSum: 0, spendSum: 0, delta: "—", loading: false, error: error.message });
        return;
      }
      const row = Array.isArray(data) && data.length ? data[0] : data;
      const count = (row && typeof row.count === "number") ? row.count : 0;
      const revenueSum = (row && typeof row.revenue_sum === "number") ? row.revenue_sum : 0;
      const spendSum = (row && typeof row.spend_sum === "number") ? row.spend_sum : 0;
      const delta = count > 0 ? `+${count} in range` : "—";
      setConversionsData({
        count,
        revenueSum,
        spendSum,
        delta,
        loading: false,
        error: null,
      });
    })();
    return () => { cancelled = true; };
  }, [advertiserId, dateRange, appliedCustomStart, appliedCustomEnd, user?.email]);

  // Pacing bar: MTD spend split by cap vs. credit (two-color segments) plus
  // the advertiser's currently-active credit terms (credit tag) — one RPC
  // round-trip covers both instead of two separate queries.
  useEffect(() => {
    if (!supabase || advertiserId == null) {
      setMtdCreditSplit((p) => ({ ...p, loading: false, error: advertiserId == null && user?.email ? "No advertiser linked" : "Supabase not configured" }));
      setActiveCredit(null);
      return;
    }
    const { startDate, endDate } = getDateRangeBounds("MTD", null, null);
    const { startISO, endISO } = toUTCStartEnd(startDate, endDate);
    let cancelled = false;
    setMtdCreditSplit((p) => ({ ...p, loading: true, error: null }));
    (async () => {
      const { data, error } = await supabase.rpc("get_mtd_spend_by_credit", {
        p_advertiser_id: advertiserId,
        p_start_at: startISO,
        p_end_at: endISO,
      });
      if (cancelled) return;
      if (error) {
        setMtdCreditSplit({ capSpend: 0, creditSpend: 0, loading: false, error: error.message });
        setActiveCredit(null);
        return;
      }
      const row = Array.isArray(data) && data.length ? data[0] : data;
      const capSpend = (row && typeof row.cap_spend === "number") ? row.cap_spend : 0;
      const creditSpend = (row && typeof row.credit_spend === "number") ? row.credit_spend : 0;
      setMtdCreditSplit({ capSpend, creditSpend, loading: false, error: null });
      setActiveCredit(row?.active_credit ?? null);
    })();
    return () => { cancelled = true; };
  }, [advertiserId, user?.email]);

  // Chart: impressions from Rill, conversions from RPC
  useEffect(() => {
    if (!supabase || advertiserId == null) {
      setChartData({ impressionsByDay: [], conversionsByDay: [], dateLabels: [], loading: false, error: null });
      return;
    }
    const { startDate, endDate } = getDateRangeBounds(dateRange, appliedCustomStart, appliedCustomEnd);
    if (startDate > endDate) {
      setChartData({ impressionsByDay: [], conversionsByDay: [], dateLabels: [], loading: false, error: null });
      return;
    }
    const { startISO, endISO } = toUTCStartEnd(startDate, endDate);
    let cancelled = false;
    setChartData((p) => ({ ...p, loading: true, error: null }));
    (async () => {
      const [rillResult, rpcResult] = await Promise.all([
        supabase.functions.invoke("rill-impressions", {
          body: { advertiser_id: advertiserId, start_date: startDate, end_date: endDate },
        }),
        supabase.rpc("get_dashboard_chart_data_hybrid", {
          p_advertiser_id: advertiserId,
          p_start_at: startISO,
          p_end_at: endISO,
        }),
      ]);
      if (cancelled) return;
      if (rillResult.error || rpcResult.error) {
        setChartData({ impressionsByDay: [], conversionsByDay: [], dateLabels: [], loading: false, error: (rillResult.error || rpcResult.error).message });
        return;
      }
      const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
      const rillByDate = {};
      for (const entry of rillResult.data || []) {
        if (!entry.date || !ISO_DATE.test(entry.date)) continue;
        const day = String(entry.date).slice(0, 10);
        rillByDate[day] = (entry.campaigns || []).reduce((s, c) => s + (c.impressions ?? 0), 0);
      }
      const convByDate = {};
      for (const r of rpcResult.data || []) {
        if (!r.day) continue;
        const day = String(r.day).slice(0, 10);
        if (ISO_DATE.test(day)) convByDate[day] = Number(r.conversions) || 0;
      }
      // Build the date axis from the union of Rill and conversion dates.
      // Do NOT cap conversions to Rill's last available date — Rill can lag several days
      // while real conversion data exists for the missing days. Days where Rill hasn't
      // reported yet will simply show 0 impressions on the chart.
      const allDates = [...new Set([
        ...Object.keys(rillByDate),
        ...Object.keys(convByDate).filter(d => d <= endDate),
      ])].sort();
      const impressionsByDay = allDates.map((d) => rillByDate[d] ?? 0);
      const conversionsByDay = allDates.map((d) => convByDate[d] ?? 0);
      const dateLabels = allDates.map((d) => {
        const date = new Date(d + "T00:00:00Z");
        if (isNaN(date.getTime())) return "";
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
      });
      setChartData({ impressionsByDay, conversionsByDay, dateLabels, loading: false, error: null });
    })();
    return () => { cancelled = true; };
  }, [advertiserId, dateRange, appliedCustomStart, appliedCustomEnd]);

  // Update lastUpdated timestamp whenever any data finishes loading
  useEffect(() => {
    if (!impressionsData.loading && !conversionsData.loading && !chartData.loading) {
      setLastUpdated(new Date());
    }
  }, [impressionsData.loading, conversionsData.loading, chartData.loading]);

  // Tick every 60s so the relative time stays current
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  function timeAgo(date) {
    if (!date) return null;
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.round(diffMs / 60_000);
    if (diffMin < 1) return "just now";
    if (diffMin === 1) return "1 min ago";
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr === 1) return "1 hour ago";
    return `${diffHr} hours ago`;
  }

  const convSpark = !chartData.loading && !conversionsData.loading && chartData.conversionsByDay.length && Math.max(...chartData.conversionsByDay) > 0
    ? chartData.conversionsByDay
    : [];

  const p = wide ? "28px 28px" : "20px 16px";

  return (
    <div style={{ display:"flex", flexDirection: wide ? "row" : "column", flex:1 }}>
      {/* Main column */}
      <div style={{ flex:1, padding:p, minWidth:0, maxWidth:1070, margin:"0 auto", width:"100%" }}>

        {/* Header */}
        <div style={{ marginTop:24, marginBottom:24 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
            <h2 style={{
              fontSize: wide ? 40 : 28, fontWeight:600, color:"#151515", margin:0, lineHeight:1.2,
              fontFamily:"'Clash Display Variable','Clash Display',sans-serif",
            }}>
              {(() => {
                const hour = new Date().getHours();
                const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
                const name = userFirstName ? userFirstName.charAt(0).toUpperCase() + userFirstName.slice(1) : null;
                return name ? `${greeting}, ${name}` : greeting;
              })()}
            </h2>

          <DateRangePicker
            dateRange={dateRange}
            setDateRange={setDateRange}
            appliedCustomStart={appliedCustomStart}
            setAppliedCustomStart={setAppliedCustomStart}
            appliedCustomEnd={appliedCustomEnd}
            setAppliedCustomEnd={setAppliedCustomEnd}
            onTrack={(props) => track('date_range_changed', { ...props, screen: 'dashboard' })}
            style={{ marginLeft: 12 }}
          />
          </div>
          <p style={{ color:"rgba(21,21,21,0.7)", fontSize:16, display:"flex", gap:5, alignItems:"center", margin:0 }}>
            Last updated {lastUpdated ? <strong style={{ fontWeight:600 }}>{timeAgo(lastUpdated)}</strong> : <strong style={{ fontWeight:600 }}>…</strong>}
          </p>
        </div>

        {/* Budget pacing bar */}
        {(() => {
          if (campaignSettingsLoading) {
            return (
              <div style={{ background:"rgba(255,255,255,0.82)", backdropFilter:"blur(15px)", WebkitBackdropFilter:"blur(15px)", border:"0.5px solid #E2E2E2", borderRadius:10, padding:"10px 20px", marginBottom:15, boxShadow:"0 3px 15px rgba(0,0,0,0.10)" }}>
                <div style={{ height:16, width:"40%", borderRadius:6, background:"linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
              </div>
            );
          }
          if (!campaignSettings) {
            return (
              <div style={{ background:"rgba(255,255,255,0.82)", backdropFilter:"blur(15px)", WebkitBackdropFilter:"blur(15px)", border:"0.5px solid #E2E2E2", borderRadius:10, padding:"10px 20px", marginBottom:15, display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, boxShadow:"0 3px 15px rgba(0,0,0,0.10)" }}>
                <span style={{ fontSize:13, color:"#626262" }}>Campaign not configured yet.</span>
                <button onClick={() => { openBudget(); track('configure_campaign_clicked'); }} style={{ fontSize:13, color:"#F90091", background:"none", border:"none", cursor:"pointer", padding:0, whiteSpace:"nowrap", flexShrink:0, fontWeight:600 }}>Configure Campaign →</button>
              </div>
            );
          }
          const spendCap = campaignSettings.spend_cap != null ? Number(campaignSettings.spend_cap) : null;
          const mtdSpend = mtdCreditSplit.loading ? null : (mtdCreditSplit.error ? null : mtdCreditSplit.capSpend + mtdCreditSplit.creditSpend);
          const pct = mtdSpend != null && spendCap != null && spendCap > 0 ? Math.min(100, (mtdSpend / spendCap) * 100) : 0;
          const overCap = mtdSpend != null && spendCap != null && mtdSpend > spendCap;
          // Two-color pacing bar: split the filled portion into cap spend vs. credit spend
          const hasCreditSplit = !mtdCreditSplit.loading && !mtdCreditSplit.error && spendCap != null && spendCap > 0;
          const capPct = hasCreditSplit ? Math.min(100, (mtdCreditSplit.capSpend / spendCap) * 100) : pct;
          const creditPct = hasCreditSplit ? Math.min(100 - capPct, (mtdCreditSplit.creditSpend / spendCap) * 100) : 0;
          return (
            <div style={{ background:"rgba(255,255,255,0.82)", backdropFilter:"blur(15px)", WebkitBackdropFilter:"blur(15px)", border:"0.5px solid #E3E3E3", borderRadius:10, padding:"14px 20px", marginBottom:15, overflow:"hidden", boxShadow:"0 3px 15px rgba(0,0,0,0.10)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10, flexWrap:"nowrap", minWidth:0 }}>
                <span style={{ fontSize:16, fontWeight:600, color:"#151515", whiteSpace:"nowrap", flexShrink:0 }}>
                  {new Date().toLocaleString("default", { month: "long" })} Spend cap
                </span>
                {campaignSettings && (
                  <>
                {(() => {
                  const eventType = Array.isArray(campaignSettings.cpa_types) && campaignSettings.cpa_types[0];
                  const fixedCpa = campaignSettings.fixed_cpa;
                  const isPct = campaignSettings.pricing_mode === PRICING_MODES.PERCENTAGE;
                  let displayRate = "—";
                  if (fixedCpa != null) {
                    displayRate = isPct ? formatPercent(fixedCpa) : formatCurrency(fixedCpa);
                  }
                  const label = eventType
                    ? `${eventType.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} ${displayRate}`
                    : displayRate;
                  return (
                    <span style={{ fontSize:14, color:"#F90091", background:"#FFE9F6", padding:"1px 8px", borderRadius:20, whiteSpace:"nowrap", flexShrink:0 }}>
                      {label}
                    </span>
                  );
                })()}
                {activeCredit?.amount != null && (() => {
                  const tooltipLines = [
                    activeCredit.percentage_rate != null && `${formatPercent(activeCredit.percentage_rate)} of conversions`,
                    activeCredit.effective_start_date && `Effective ${formatCreditDateShort(activeCredit.effective_start_date)}–${activeCredit.effective_end_date ? formatCreditDateShort(activeCredit.effective_end_date) : "ongoing"}`,
                  ].filter(Boolean);
                  return (
                    <TooltipChip
                      trigger="click"
                      style={{ fontSize:14, color:"#208a3e", background:"#E6F7EE", padding:"1px 8px", borderRadius:20, whiteSpace:"nowrap", flexShrink:0, display:"inline-block" }}
                      tooltip={tooltipLines.map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>)}
                    >
                      Credit {formatCurrency(activeCredit.amount)}
                    </TooltipChip>
                  );
                })()}
                {campaignSettings.status === 'pending' && (
                  <PendingChip />
                )}
                  </>
                )}
                <div style={{ flex:1, minWidth:0 }} />
                {mtdCreditSplit.loading ? (
                  <span style={{ fontSize:14, color:"#7b7b7b", whiteSpace:"nowrap", flexShrink:0 }}>Loading…</span>
                ) : mtdCreditSplit.error ? (
                  <span style={{ fontSize:14, color:"#e53935", whiteSpace:"nowrap", flexShrink:0 }}>{mtdCreditSplit.error}</span>
                ) : overCap ? (
                  <span style={{ fontSize:14, color:"#e53935", whiteSpace:"nowrap", flexShrink:0 }}>Over cap</span>
                ) : (
                  <span style={{ fontSize:14, color:"#3E3E3E", whiteSpace:"nowrap", flexShrink:0 }}>On track</span>
                )}
                <button onClick={openBudget} style={{ fontSize:14, color:"#F90091", background:"none", border:"none", cursor:"pointer", padding:"0 0 0 6px", whiteSpace:"nowrap", flexShrink:0 }}>Edit →</button>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ flex:1, display:"flex", background:"#E2E2E2", borderRadius:4, height:6, overflow:"hidden" }}>
                  <div style={{ width:`${capPct}%`, height:"100%", background:"linear-gradient(to right, #F90091, #9900FF)" }} />
                  {creditPct > 0 && (
                    <div style={{ width:`${creditPct}%`, height:"100%", background:"#208a3e" }} />
                  )}
                </div>
                <span style={{ fontSize:14, color:"#7b7b7b", whiteSpace:"nowrap" }}>
                  {mtdCreditSplit.loading ? "…" : mtdCreditSplit.error ? "—" : `$${Number(mtdSpend ?? 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                  {spendCap != null && <span>{" "}/ {`$${Number(spendCap).toLocaleString()}`}</span>}
                </span>
              </div>
              {hasCreditSplit && mtdCreditSplit.creditSpend > 0 && (
                <div style={{ display:"flex", gap:14, marginTop:6, fontSize:12, color:"#7b7b7b" }}>
                  <span><span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", background:"linear-gradient(to right, #F90091, #9900FF)", marginRight:5 }} />Cap spend: ${Number(mtdCreditSplit.capSpend).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                  <span><span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", background:"#208a3e", marginRight:5 }} />Credit spend: ${Number(mtdCreditSplit.creditSpend).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                </div>
              )}
            </div>
          );
        })()}

        {/* KPI row — 2×2 on mobile, 4×1 on desktop */}
        <div style={{ display:"grid", gridTemplateColumns: wide ? "repeat(4,1fr)" : "repeat(2,1fr)", gap:10, marginBottom:15 }}>
          {[
            {
              label: "SPEND",
              loading: conversionsData.loading,
              value: conversionsData.error ? "—" : `$${Number(conversionsData.spendSum).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
              delta: conversionsData.error ? conversionsData.error : conversionsData.delta,
              spark: convSpark,
              deltaColor: conversionsData.error ? "#e53935" : "#22c55e",
            },
            {
              label: "REVENUE",
              loading: conversionsData.loading,
              value: conversionsData.error ? "—" : `$${Number(conversionsData.revenueSum).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
              delta: conversionsData.error ? conversionsData.error : conversionsData.delta,
              spark: convSpark,
              deltaColor: conversionsData.error ? "#e53935" : "#22c55e",
            },
            {
              label: "IMPRESSIONS",
              loading: impressionsData.loading,
              value: impressionsData.error ? "—" : formatImpressions(impressionsData.total),
              delta: impressionsData.error ? impressionsData.error : impressionsData.delta,
              spark: impressionsData.loading || chartData.loading ? [] : (impressionsData.spark.length && Math.max(...impressionsData.spark) > 0 ? impressionsData.spark : convSpark),
              deltaColor: impressionsData.error ? "#e53935" : "#22c55e",
            },
            {
              label: "CONVERSIONS",
              loading: conversionsData.loading,
              value: conversionsData.error ? "—" : conversionsData.count.toLocaleString(),
              delta: conversionsData.error ? conversionsData.error : conversionsData.delta,
              spark: convSpark,
              deltaColor: conversionsData.error ? "#e53935" : "#22c55e",
            },
          ].map(k => (
            <div key={k.label} style={{
              background:"rgba(255,255,255,0.82)", backdropFilter:"blur(15px)", WebkitBackdropFilter:"blur(15px)",
              border:"0.5px solid #E2E2E2", borderRadius:10,
              padding: wide ? "15px 12px" : "12px 10px",
              boxShadow:"0 3px 15px rgba(0,0,0,0.10)",
            }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <div style={{ fontSize:14, color:"#626262", marginBottom:5, letterSpacing:0.5 }}>{k.label}</div>
                  {k.loading ? (
                    <>
                      <div style={{ height:24, width:80, borderRadius:6, marginBottom:6, background:"linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
                      <div style={{ height:10, width:50, borderRadius:4, background:"linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
                    </>
                  ) : (
                    <>
                      <div style={{
                        fontSize: wide ? 23 : 18, fontWeight:600, color:"#151515", marginBottom:4, lineHeight:1.2,
                        fontFamily:"'Clash Display Variable','Clash Display',sans-serif",
                      }}>{k.value}</div>
                      <div style={{ fontSize:10, color:k.deltaColor }}>{k.delta}</div>
                    </>
                  )}
                </div>
                {wide && <div style={{ width:70 }}><Sparkline data={k.spark} color="#22c55e" height={36} /></div>}
              </div>
            </div>
          ))}
        </div>

        {/* Area chart */}
        <div style={{ background:"rgba(255,255,255,0.82)", backdropFilter:"blur(15px)", WebkitBackdropFilter:"blur(15px)", border:"0.5px solid #E2E2E2", borderRadius:14, padding: wide ? "20px 22px 16px" : "16px 14px 16px", boxShadow:"0 3px 8px rgba(0,0,0,0.10)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ fontWeight:400, color:"#3E3E3E", fontSize:14 }}>
              Performance
              {(() => {
                const { startDate, endDate } = getDateRangeBounds(dateRange, appliedCustomStart, appliedCustomEnd);
                if (startDate && endDate) {
                  const start = new Date(startDate + "T00:00:00Z");
                  const end = new Date(endDate + "T00:00:00Z");
                  return " · " + start.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) + " – " + end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
                }
                return "";
              })()}
            </div>
            <div style={{ display:"flex", gap:12, fontSize:12, fontWeight:500 }}>
              {CHART_LEGEND.map(({ key, label, color }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setVisibleSeries((p) => ({ ...p, [key]: !p[key] }))}
                  style={{ background:"none", border:"none", padding:0, cursor:"pointer", color: visibleSeries[key] ? color : "#B8B8B8", textDecoration: visibleSeries[key] ? "none" : "line-through" }}
                >
                  — {label}
                </button>
              ))}
            </div>
          </div>
          {chartData.loading && <div style={{ padding: 40, textAlign: "center", color: "#7b7b7b", fontSize: 13 }}>Loading chart…</div>}
          {chartData.error && <div style={{ padding: 40, textAlign: "center", color: "#e53935", fontSize: 13 }}>{chartData.error}</div>}
          {!chartData.loading && !chartData.error && (
            <AreaChart
              height={240}
              impressionsByDay={chartData.impressionsByDay}
              conversionsByDay={chartData.conversionsByDay}
              dateLabels={chartData.dateLabels}
              showImpressions={visibleSeries.impressions}
              showConversions={visibleSeries.conversions}
            />
          )}
        </div>

      </div>
    </div>
  );
}
